import { useEffect, useState } from 'react';

import useOffers from 'api/useOffers';
import { Offer } from 'common/contracts';
import { OfferType } from 'constants/enums';
import { OfferDesignSelector } from 'views/Offers/components/OfferDesignSelector';
import { SegmentSelector } from 'views/Offers/components/SegmentSelector';
import { SettingsTabProps } from 'views/Offers/RollingOffers/Form/RollingOfferForm.types';

import Input from 'components/Input/Input';
import RichText from 'components/RichText/RichText';

const SettingsTab: React.FC<SettingsTabProps> = ({
  formikProps,
  rollingOfferId,
  edit,
  chosenSegment,
  setChosenSegment,
  offerDesignOptions,
  data,
  dup
}) => {
  const { handleChange, handleBlur, touched, errors, setFieldValue, values } =
    formikProps;

  const [isNameExists, setIsNameExists] = useState<boolean>(false);

  const { getOffers } = useOffers({
    offerId: rollingOfferId,
    offerType: OfferType.ROLLING_OFFER
  });


  const isNameError = (touched.name && errors.name) || isNameExists;
  const nameErrorMessage =
    (touched.name ? errors.name?.toString() : '') ||
    (isNameExists ? 'Name already exists' : '');

  const isExternalIdError =
    Boolean(errors.publisherOfferId) && (touched.publisherOfferId || dup);
  const externalIdErrorMessage =
    touched.publisherOfferId || dup ? errors.publisherOfferId?.toString() : '';

  const offerDesignErrorMessage =
    touched.offerUiId && errors.offerUiId ? errors.offerUiId?.toString() : '';

  useEffect(() => {
    const offerExists = getOffers.data?.offers.some(
      (offer: Offer) => offer.name === values.name
    );
    setIsNameExists(offerExists && !edit);
  }, [values.name, edit, getOffers.data]);

  useEffect(() => {
    if (data?.segments) {
      setChosenSegment(data.segments);
    }
  }, [data?.segments, setChosenSegment]);



  return (
    <div className="flex flex-col">
      <RichText
        title="General"
        description="Complete the required fields for your offer."
        className="mb-5"
      />
      <div className="flex flex-col gap-9">
        <div className="grid grid-cols-2 gap-5">
          <Input
            id="rolling-offer-name"
            label="Name"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            placeholder="Name"
            state={isNameError ? 'error' : 'default'}
            description={nameErrorMessage}
            dataTestId="rollingOfferNameInput"
          />
          <Input
            id="rolling-offer-external-id"
            label="External ID"
            name="publisherOfferId"
            value={values.publisherOfferId}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            placeholder="External ID"
            state={isExternalIdError ? 'error' : 'default'}
            description={externalIdErrorMessage}
            dataTestId="rollingOfferIdInput"
          />
        </div>
        <div className="grid grid-cols-2 gap-5">
          <OfferDesignSelector
            options={offerDesignOptions}
            selectedValue={values.offerUiId}
            onChange={(value) => setFieldValue('offerUiId', value)}
            listDataTestId="dashRollingOfferDesignList"
            buttonDataTestId="rollingOfferDesignBox"
            placeholder="Select"
            description={offerDesignErrorMessage}
          />
          <Input
            id="rolling-offer-priority"
            label="Priority"
            name="priority"
            value={values.priority}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            type="number"
            placeholder="1"
            min={1}
            max={10}
            tooltipIcon="fa-circle-info"
            infoTooltipContent="Priority decides which rolling offer show, based on their importance. The lower the number, the higher the priority."
          />
        </div>
      </div>
      {/* data-testid="rollingOfferSegmentsList" -- propagated via listTestId to SegmentSelector */}
      <SegmentSelector
        segments={chosenSegment}
        onSegmentsChange={(newSegments) => {
          setChosenSegment(newSegments);
          setFieldValue('segments', newSegments);
        }}
        inputTestId="rollingOfferSegmentsInput"
        listTestId="rollingOfferSegmentsList"
        placeholder="Search or create new"
        description="Create or add players segments to your offer."
      />
    </div>
  );
};

export default SettingsTab;
